/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import '@mui/lab';
import {FormControlLabel} from '@mui/material';
import {TreeItem} from '@mui/x-tree-view';
import {SimpleTreeView} from '@mui/x-tree-view/SimpleTreeView';
import {useEffect, useState} from 'react';
import ArgosFlexbox from '../../../../../../../components/Containers/ArgosFlexbox';
import ArgosCheckbox from '../../../../../../../components/Form/Checkbox/ArgosCheckbox';
import {ArgosCollapseNodeIcon, ArgosExpandNodeIcon} from '../../../../../../../components/Icons/ArgosTreeIcons';
import {SINGLE_DATASET_TYPE_STR} from "../../../../constants";
import {ITreeNode} from '../../LeftPanelComponents/TreeComponent/CoverageRequirementsTreeComponent';
import {getObjectDisplayName} from "../DrivingScenarioProperties";
import { usePostRequest } from '../../../../../../../../api/helper';
import { IFetchObjectsResponse, IProcessedObjectData } from '../../../../../../../../constants/objects/ObjectTypes/common';
import { getObjVersionDisplayString, processObjectData } from '../../../../../../../../helpers/objects/helpers';
import { fetchRwoDataEndpoint } from '../constants';

const convertLineDataToTreeData = (lineDataList: any, objCatToData: IProcessedObjectData) => {
  let nodeCounter = 1
  let nodesList: any = []
  Object.keys(lineDataList)?.forEach((objCat: string, index: number) => {
    nodeCounter += 1
    const lineNode: ITreeNode = {
      nodeId: String(nodeCounter),
      nodeType: "category",
      children: [],
      name: objCat
    }
    lineDataList[objCat].forEach((objectElement: any, index: number) => {
      // Get the version from the object data given the object ID
      const versionString: string = getObjVersionDisplayString(objectElement.objectId, objCatToData);
      nodeCounter += 1
      lineNode.children?.push({
        nodeId: String(nodeCounter),
        nodeType: "object",
        children: [],
        name: getObjectDisplayName(null, objectElement.name, objectElement.variantName, versionString),
        objectId: objectElement.objectId
      })
    })
    nodesList.push(lineNode)
  })
  return nodesList
}

interface AnalysisResultsLeftPanelProps {
  data: any
  datasetType: string
  plottingList: string[] // List of selected object ids to show data on plot
  setPlottingList: Function
  selectedCategories: string[] // List of selected categories to show data on plot
  setSelectedCategories: Function
  preselectedObjects: string[] // List of all possible objects to select from
  isNewReportGraph?: boolean
}

const MAX_OBJECTS_TO_PLOT = Infinity  /** The number of objects that can be selected at one time for plotting */

/**
 * A checklist displayed on the left hand side of analysis results, allowing users to select objects from a checklist to plot.
 * 
 * @returns {JSX.Element} The rendered component.
 */
const AnalysisResultsLeftPanel = (props: AnalysisResultsLeftPanelProps) => {
  // State to store the processed object data
  const [objCatToData, setObjCatToData] = useState<IProcessedObjectData>({})
  const treeData = convertLineDataToTreeData(props?.data, objCatToData)
  // Setup for fetching object data
  const fetchObjectsHandle = usePostRequest(fetchRwoDataEndpoint)
  /**
   * Fetches object data and processes it to set the state.
   * This function fetches the objects from the redline dataset and processes them to create a mapping of object categories to their data.
   */
  const fetchObjectData = () => {
    fetchObjectsHandle().then((fetchObjectsResponse: IFetchObjectsResponse) => {
      const processedObjectData = processObjectData(fetchObjectsResponse)
      setObjCatToData(processedObjectData);
    }).catch((err) => {
      console.log(err)
    })
  }

  useEffect(() => {
    fetchObjectData()
    // override plotting list and selected categories with preselected objects (object name is a property object.name)
    let localPlottingList: string[] = []
    let localCategoriesList: string[] = []
    if(!(props.plottingList?.length > 0) && props.preselectedObjects?.length > 0) {
      props.preselectedObjects.forEach((objectId: any) => {
        localPlottingList.push(objectId)
        const parent = getObjectCategoryFromId(objectId)
        if (localCategoriesList.indexOf(parent) == -1) {
          localCategoriesList.push(parent)
        }
      })
      props.setPlottingList(localPlottingList)
    }
    props.setSelectedCategories(localCategoriesList)
  }, [props.preselectedObjects, props.data]);

  const handleToggle = (value: string, elementType: string) => () => {
    const parent = getObjectCategoryFromId(value)
    let localPlottingList: any[] = [...props.plottingList]
    let categoriesList = [...props.selectedCategories]

    if (elementType == "category") {
      let sublist: string[] = props?.data[value]
      if (!sublist) return
      sublist = sublist.map((x: any) => x.objectId)
      if (categoriesList.indexOf(value) == -1) {
        localPlottingList = localPlottingList.concat(sublist)
        categoriesList.push(value)
      } else {
        categoriesList.splice(categoriesList.indexOf(value), 1)
        const subListAsSet = new Set(sublist)
        localPlottingList = localPlottingList.filter((x: any) => !subListAsSet.has(x))
      }
    } else {
      const index: number = localPlottingList.indexOf(value)
      let subList = props?.data[parent]
      if (!subList) return
      subList = subList.map((x: any) => x.objectId)
      if (index == -1) {
        localPlottingList.push(value)
        if (categoriesList.indexOf(parent) == -1) {
          categoriesList.push(parent)
        }
      } else {
        let noChildInList = true
        localPlottingList.splice(index, 1)
        subList?.forEach((subElement: string) => {
          if (localPlottingList.indexOf(subElement) != -1) {
            noChildInList = false
          }
        })
        if (noChildInList) {
          categoriesList.splice(categoriesList.indexOf(parent), 1)
        }
      }
    }
    if (localPlottingList.length > MAX_OBJECTS_TO_PLOT && !props.isNewReportGraph) {
      localPlottingList = localPlottingList.slice(0, MAX_OBJECTS_TO_PLOT)
    }
    props.setPlottingList(localPlottingList)
    props.setSelectedCategories(categoriesList)
  };

  const getObjectCategoryFromId = (objectId: string): string => {
    let objects = props?.data
    let parent = ""
    Object.keys(objects).every((objectCategory: string) => {
      const index = getIndexGivenObjectId(objects[objectCategory], objectId)
      if (index != -1) {
        parent = objectCategory
        return false
      } else {
        return true
      }
    })
    return parent
  }

  const getIndexGivenObjectId = (objectsList: any[], objectId: string): number => {
    for (let i = 0; i < objectsList.length; i++) {
      if (objectsList[i].objectId === objectId) {
        return i;
      }
    }
    return -1;
  }

  const isNodeChecked = (node: ITreeNode) => {
    if (!node) {
      return false
    }
    if (node.nodeType == "category") {
      return props.selectedCategories.indexOf(node.name) != -1
    } else {
      const val = node.objectId != null && props.plottingList.includes(node.objectId);
      return val
    }
  }

  const renderTree = (_treeData: any) => {
    return (_treeData?.map((treeNode: ITreeNode) => {
        return (
          <TreeItem
            key={treeNode?.nodeId}
            itemId={treeNode?.nodeId}
            label={
              <FormControlLabel
                control={ // no checkboxes for raw dataset
                  props?.datasetType == SINGLE_DATASET_TYPE_STR ? <div style={{padding: 5}}></div> :
                    <ArgosCheckbox
                      checked={isNodeChecked(treeNode)}
                      disabled={props?.datasetType == SINGLE_DATASET_TYPE_STR
                        || (props.plottingList.length >= MAX_OBJECTS_TO_PLOT && !isNodeChecked(treeNode) && !props.isNewReportGraph) // disable checkbox if max number of objects is reached and not already checked and not in new report graph mode
                    }
                      onChange={handleToggle(treeNode?.objectId ?? treeNode.name, treeNode.nodeType)}
                      onClick={e => e.stopPropagation()}
                    />
                }
                label={treeNode.name}
                key={treeNode?.nodeId}
                onClick={e => e.preventDefault()}
              />
            }>
            {treeNode?.children?.length != 0 && renderTree(treeNode.children)}
          </TreeItem>
        )
      }
    ))
  }

  // expand the top level nodes
  const defaultExpanded = treeData?.map((treeNode: ITreeNode) => treeNode.nodeId)
  return (
    <ArgosFlexbox vertical style={{width: "25%"}}>
      {props?.data.length != 0 &&
        <SimpleTreeView
          aria-label="file system navigator"
          slots={{
            collapseIcon: ArgosExpandNodeIcon,
            expandIcon: ArgosCollapseNodeIcon
          }}
          sx={{flexGrow: 1, maxWidth: '100%', overflowY: 'auto'}}
          defaultExpandedItems={defaultExpanded}
          key={'analysisres-' + defaultExpanded?.length} // This causes the tree to re-render if defaultExpanded length changes
        >
          {renderTree(treeData)}
        </SimpleTreeView>
      }
    </ArgosFlexbox>
  );
}
export {AnalysisResultsLeftPanel};
